/**
 * Action checklist for a role binding rule.
 */
import React from 'react';
import { Info } from 'lucide-react';
import { Checkbox } from '@/components/forms/Checkbox';
import { ReadScopeBuilder } from './ReadScopeBuilder';
import {
  categorizeAction,
  filterActionsByTargetRole,
  getActionVisual,
  groupActions,
  partitionActionsByRoleSubtype,
  toggleActionWithWriteScopes,
} from '@/components/roles/role-form-utils';
import { cn } from '@/lib/utils';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type { InterfaceElement } from '@/types/interface-elements.types';
import type { RuleReadScope } from '@/types/role.types';

/** Props for {@link ActionsChecklist}. */
interface ActionsChecklistProps {
  actions: InterfaceElement[];
  selectedActionIds: string[];
  onChange: (actionIds: string[]) => void;
  writeScopes?: Record<string, RuleReadScope | null>;
  onWriteScopesChange?: (writeScopes: Record<string, RuleReadScope | null>) => void;
  disabled?: boolean;
  targetRoleCode?: string; // For per-tab filtering (role-group modules)
  visibilityScope?: RuleReadScope | null; // Constrains write scopes
}

/** Action checklist for a role binding rule. */
export function ActionsChecklist({
  actions,
  selectedActionIds,
  onChange,
  writeScopes = {},
  onWriteScopesChange,
  disabled,
  targetRoleCode,
  visibilityScope,
}: Readonly<ActionsChecklistProps>): React.ReactElement | null {
  const filteredActions = React.useMemo(
    () => filterActionsByTargetRole(actions, targetRoleCode),
    [actions, targetRoleCode],
  );

  const { regularActions, createRoleActions, editRoleActions, deleteRoleActions } =
    partitionActionsByRoleSubtype(filteredActions);

  const actionGroups = groupActions(regularActions);

  const toggleAction = (actionId: string) => {
    if (disabled) return;

    const result = toggleActionWithWriteScopes(
      selectedActionIds,
      actionId,
      writeScopes,
      visibilityScope,
    );
    onChange(result.selectedActionIds);
    if (result.writeScopesChanged) {
      onWriteScopesChange?.(result.writeScopes);
    }
  };

  const handleWriteScopeChange = (actionId: string, scope: RuleReadScope | null) => {
    if (disabled || !onWriteScopesChange) return;
    onWriteScopesChange({ ...writeScopes, [actionId]: scope });
  };

  if (
    regularActions.length === 0 &&
    createRoleActions.length === 0 &&
    editRoleActions.length === 0 &&
    deleteRoleActions.length === 0
  ) {
    return (
      <div className="text-sm text-gray-500 italic">Aucune action disponible pour ce module</div>
    );
  }

  const renderActionItem = (action: InterfaceElement) => {
    const isChecked = selectedActionIds.includes(action.elementId);
    const hasWriteScope = onWriteScopesChange !== undefined;
    const currentWriteScope = writeScopes[action.elementId] || null;
    const { Icon, iconBgClass, iconClass } = getActionVisual(action);

    const category = categorizeAction(action);
    const supportsWriteScope = category !== 'creation' && category !== 'export_import';

    return (
      <div
        key={action.id}
        className={cn(
          'rounded-lg border transition-all',
          isChecked
            ? 'border-blue-200 bg-blue-50/40 shadow-sm'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
        )}
      >
        <div className="flex items-center gap-3 p-3">
          <div
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              iconBgClass,
            )}
            aria-hidden
          >
            <Icon className={cn('h-4 w-4', iconClass)} strokeWidth={2.25} />
          </div>

          <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 leading-snug">
            {action.elementLabel}
          </span>

          <Checkbox
            checked={isChecked}
            onCheckedChange={() => toggleAction(action.elementId)}
            disabled={disabled}
          />
        </div>

        {isChecked && hasWriteScope && supportsWriteScope && (
          <div className="px-3 pb-3 pt-0">
            <div className="rounded-lg bg-white border border-blue-100 p-3">
              <ReadScopeBuilder
                value={currentWriteScope}
                onChange={(scope) => handleWriteScopeChange(action.elementId, scope)}
                disabled={disabled}
                label="Sur quelles données ?"
                constrainedBy={visibilityScope ?? null}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {regularActions.length > 0 && (
        <div className="space-y-6">
          {actionGroups.map((group, groupIndex) => (
            <div key={group.category}>
              {groupIndex > 0 && <div className="border-t border-gray-200 -mx-4 px-4" />}
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {group.label}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {group.actions.map(renderActionItem)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(createRoleActions.length > 0 ||
        editRoleActions.length > 0 ||
        deleteRoleActions.length > 0) && (
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-start gap-2 text-xs text-blue-900">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                Ces options définissent quels types d'utilisateurs ce rôle peut créer, modifier ou
                supprimer. Cocher "{getRoleLabel('coordinator')}" dans "Créer" autorise la création
                de {getRoleLabelPlural('coordinator').toLowerCase()}; cocher dans "Modifier"
                autorise leur modification ; cocher dans "Supprimer" autorise leur suppression.
              </p>
            </div>
          </div>

          {createRoleActions.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                Peut créer ces types d'utilisateurs
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {createRoleActions.map(renderActionItem)}
              </div>
            </div>
          )}

          {editRoleActions.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                Peut modifier ces types d'utilisateurs
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {editRoleActions.map(renderActionItem)}
              </div>
            </div>
          )}

          {deleteRoleActions.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                Peut supprimer ces types d'utilisateurs
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {deleteRoleActions.map(renderActionItem)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
